import { Flex, Typography } from "antd"
import type { TFunction } from "i18next"
import React from "react"
import { useTranslation } from "react-i18next"

import { requestClock } from "../../helpers/api-time.ts"
import { formatElapsed } from "../../helpers/elapsed.ts"
import {
  type RecipientLine,
  recipientLine
} from "../../helpers/recipient-line.ts"
import type { TrackedEnvelope } from "../../types/tracking.types.ts"

const { Text } = Typography

function describe(line: RecipientLine, t: TFunction) {
  const age = "seconds" in line ? formatElapsed(line.seconds, t) : ""
  switch (line.kind) {
    case "not_sent":
      return {
        text: t("esign.tracking.recipient.notSent"),
        tone: "secondary" as const
      }
    case "declined":
      return {
        text: t("esign.tracking.recipient.declined"),
        tone: "danger" as const
      }
    case "bounced":
      return {
        text: t("esign.tracking.recipient.bounced"),
        tone: "danger" as const
      }
    case "signed":
      return {
        text: t("esign.tracking.recipient.signedAfter", { age }),
        tone: "success" as const
      }
    case "countersigned":
      return {
        text: t("esign.tracking.recipient.countersignedAfter", { age }),
        tone: "success" as const
      }
    case "waiting_on_us":
      return {
        text: t("esign.tracking.recipient.waitingOnUs", { age }),
        tone: "warning" as const
      }
    case "opened":
      return {
        text: t("esign.tracking.recipient.opened", { count: line.opens, age }),
        tone: undefined
      }
    case "stuck_at_code":
      return {
        text: t("esign.tracking.recipient.stuckAtCode", { age }),
        tone: "warning" as const
      }
    case "not_clicked":
      return {
        text: t("esign.tracking.recipient.notClicked", { age }),
        tone: "warning" as const
      }
  }
}

export function RecipientLines({ envelope }: { envelope: TrackedEnvelope }) {
  const { t } = useTranslation()
  const now = requestClock(envelope.ended_at)
  const ended = Boolean(envelope.ended_at)
  return (
    <Flex vertical gap={2}>
      {envelope.recipients.map((recipient) => {
        const described = describe(recipientLine(recipient, now), t)
        // An interval on a request that ended is no longer running, so it is no cause for alarm.
        const tone =
          ended && described.tone === "warning" ? undefined : described.tone
        const { text } = described
        return (
          <div key={recipient.id} className={"leading-tight"}>
            <Text className={"text-sm"}>{recipient.name}</Text>
            <Text type={tone} className={"block text-xs"}>
              {text}
            </Text>
          </div>
        )
      })}
    </Flex>
  )
}
